import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { getFeeds } from "../services/socialAuth";
import FeedItem from "./FeedItem";
import { getProfileImageWithFallback } from "../utils/imageUtils";
import "./FeedList.css";

interface Feed {
  id: string;
  userId: string;
  userInfo: {
    name: string;
    profileImage?: string;
  };
  content: string;
  hashtags?: string[];
  mediaFiles: Array<{
    url: string;
    type: string;
    name: string;
    size: number;
  }>;
  locationInfo?: {
    latitude: number;
    longitude: number;
    address?: string;
  };
  likeCount: number;
  commentCount: number;
  createdAt: number;
  updatedAt: number;
}

interface FeedListProps {
  category?: "all" | "user" | "petmilly";
}

const FeedList: React.FC<FeedListProps> = ({ category = "all" }) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [feeds, setFeeds] = useState<Feed[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [lastKey, setLastKey] = useState<string | null>(null);

  // 피드 데이터 로드 함수를 useCallback으로 래핑
  const loadFeeds = useCallback(
    async (isInitial = false) => {
      try {
        setLoading(true);
        setError(null);

        const params: any = {
          limit: 20,
          category: category === "user" && currentUser ? "user" : "all",
        };

        if (category === "user" && currentUser) {
          params.userId = currentUser.uid;
        }

        if (!isInitial && lastKey) {
          params.lastKey = lastKey;
        }

        const result = await getFeeds(params);

        if (isInitial) {
          setFeeds(result.feeds);
        } else {
          setFeeds((prev) => [...prev, ...result.feeds]);
        }

        setHasMore(result.hasMore);
        setLastKey(result.lastKey);
      } catch (err) {
        console.error("피드 로드 오류:", err);
        setError(
          err instanceof Error ? err.message : "피드를 불러오는데 실패했습니다."
        );
      } finally {
        setLoading(false);
      }
    },
    [category, currentUser, lastKey]
  );

  // 초기 로드
  useEffect(() => {
    loadFeeds(true);
  }, [loadFeeds]);

  // "내피드" 카테고리에서 로그인하지 않은 경우 처리
  if (category === "user" && !currentUser) {
    return (
      <div className="feed-list-empty">
        <img
          src={`${process.env.PUBLIC_URL}/images/icons/feed-llist.svg`}
          alt="로그인 필요"
          className="empty-icon"
        />
        <h3>로그인이 필요합니다</h3>
        <p>내 피드를 보려면 로그인해주세요!</p>
        <button
          onClick={() => navigate("/login")}
          className="retry-button"
          style={{ marginTop: "16px" }}
        >
          로그인하기
        </button>
      </div>
    );
  }

  // 시간 포맷팅 함수
  const formatTime = (timestamp: number): string => {
    const now = Date.now();
    const diff = now - timestamp;

    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days}일 전`;
    } else if (hours > 0) {
      return `${hours}시간 전`;
    } else if (minutes > 0) {
      return `${minutes}분 전`;
    } else {
      return "방금 전";
    }
  };

  // Feed 데이터를 FeedItem props로 변환
  const convertFeedToProps = (feed: Feed) => ({
    id: feed.id,
    profileImage: getProfileImageWithFallback(feed.userInfo.profileImage),
    nickname: feed.userInfo.name,
    userType: "일반", // TODO: 사용자 타입 정보 추가 필요
    petInfo: "반려동물 정보", // TODO: 반려동물 정보 추가 필요
    content: feed.content,
    mediaFiles:
      feed.mediaFiles?.map((media) => ({
        url: media.url,
        type: media.type,
        name: media.name,
      })) || [],
    tags: feed.hashtags || [],
    postedAt: formatTime(feed.createdAt),
    comments: [], // TODO: 댓글 데이터 처리 필요
    initialLikeCount: feed.likeCount || 0,
    initialIsLiked: false, // TODO: 사용자별 좋아요 상태 확인 필요
  });

  // 로딩 중
  if (loading && feeds.length === 0) {
    return (
      <div className="feed-list-loading">
        <div className="loading-spinner"></div>
        <p>피드를 불러오는 중...</p>
      </div>
    );
  }

  // 오류 발생
  if (error) {
    return (
      <div className="feed-list-error">
        <p>오류가 발생했습니다: {error}</p>
        <button onClick={() => loadFeeds(true)} className="retry-button">
          다시 시도
        </button>
      </div>
    );
  }

  // 피드가 없는 경우
  if (feeds.length === 0) {
    return (
      <div className="feed-list-empty">
        <img
          src={`${process.env.PUBLIC_URL}/images/icons/feed-llist.svg`}
          alt="피드 없음"
          className="empty-icon"
        />
        <h3>아직 피드가 없습니다</h3>
        <p>첫 번째 피드를 작성해보세요!</p>
      </div>
    );
  }

  return (
    <div className="feed-list">
      {feeds.map((feed) => (
        <FeedItem key={feed.id} {...convertFeedToProps(feed)} />
      ))}

      {/* 더 보기 버튼 */}
      {hasMore && (
        <div className="feed-list-load-more">
          <button
            onClick={() => loadFeeds(false)}
            disabled={loading}
            className="load-more-button"
          >
            {loading ? "로딩 중..." : "더 보기"}
          </button>
        </div>
      )}

      {/* 하단 로딩 */}
      {loading && feeds.length > 0 && (
        <div className="feed-list-loading-more">
          <div className="loading-spinner-small"></div>
        </div>
      )}
    </div>
  );
};

export default FeedList;
